import React from 'react';
import { ChevronDown, ChevronUp, Plus, Save, X } from 'lucide-react';

import {
  Card, PhonePage, PrimaryButton, SearchBar, TopBar,
} from '@/components/phone/primitives';
import { Field, INPUT_CLASS, inputStyle, SelectField } from '@/components/phone/FieldControls';
import { Colors } from '@/components/phone/theme';
import PhoneSpecFields from './PhoneSpecFields';

/**
 * Editing a product, on a phone.
 *
 * The same three cards as the desktop — the basics, the job card template, the
 * customer prices — as 48px controls under their labels, with the template in
 * the supervisor app's field controls. The state lives on the page; this only
 * draws it, so the phone saves exactly what the desktop saves.
 */

const STATUSES = [{ value: 'active', label: 'Active' }, { value: 'inactive', label: 'Inactive' }];

export default function PhoneEditCatalogItem({
  onBack,
  itemName, setItemName, productType, status, setStatus,
  basePrice, setBasePrice, minStockThreshold, setMinStockThreshold,
  jobCardFields, jobCardFieldValues, onFieldChange, onPlyChange,
  showPricing, setShowPricing, existingPrices, customers, onCustomerSearch,
  newPriceCustomer, setNewPriceCustomer, newPriceValue, setNewPriceValue,
  onAddPrice, onRemovePrice,
  saving, onSubmit,
}) {
  const customerOptions = customers
    .filter((c) => !existingPrices.some((cp) => cp.customer_id === c.id))
    .map((c) => ({ value: c.id, label: `${c.customer_name}${c.firm_name ? ` (${c.firm_name})` : ''}` }));

  return (
    <PhonePage testID="edit-catalog-item-page">
      <TopBar
        eyebrow="CATALOG"
        title="Edit catalog item"
        subtitle="Changes apply to future orders only"
        onBack={onBack}
      />

      <div className="flex-1 px-4 pb-10">
        <Card className="mt-3" title="Basic information">
          <Field label="Item name *">
            <input
              value={itemName} onChange={(e) => setItemName(e.target.value)}
              data-testid="item-name" className={INPUT_CLASS} style={inputStyle}
            />
          </Field>
          <Field label="Product type" className="mt-3">
            <input value={productType} disabled aria-label="Product type" className={INPUT_CLASS} style={{ ...inputStyle, backgroundColor: Colors.background.tertiary }} />
            <p className="mt-1 text-[11px]" style={{ color: Colors.text.tertiary }}>Cannot be changed after creation</p>
          </Field>
          <Field label="Status" className="mt-3">
            <SelectField name="status" label="Status" value={status} options={STATUSES} onChange={setStatus} />
          </Field>
          <div className="mt-3 grid grid-cols-2 gap-3">
            <Field label="Base price per piece">
              <input
                type="number" inputMode="decimal" step="0.01" value={basePrice} placeholder="₹ 0.00"
                onChange={(e) => setBasePrice(e.target.value)}
                data-testid="base-price" className={`${INPUT_CLASS} text-right tabular-nums`} style={inputStyle}
              />
            </Field>
            <Field label="Minimum stock">
              <input
                type="number" inputMode="numeric" value={minStockThreshold}
                onChange={(e) => setMinStockThreshold(e.target.value)}
                data-testid="min-stock" className={`${INPUT_CLASS} text-right tabular-nums`} style={inputStyle}
              />
            </Field>
          </div>
        </Card>

        {jobCardFields.length > 0 && (
          <PhoneSpecFields
            fields={jobCardFields}
            values={jobCardFieldValues}
            onChange={onFieldChange}
            onPlyChange={onPlyChange}
            orderType={productType}
          />
        )}

        <Card className="mt-3" testID="customer-pricing">
          <button
            type="button"
            onClick={() => setShowPricing(!showPricing)}
            aria-expanded={showPricing}
            data-testid="toggle-pricing"
            className="flex h-11 w-full items-center justify-between gap-2 text-left"
          >
            <span className="text-[15px] font-bold" style={{ color: Colors.text.primary }}>
              Customer pricing
              {existingPrices.length > 0 && (
                <span className="ml-2 text-[11px] font-medium" style={{ color: Colors.text.tertiary }}>{existingPrices.length} linked</span>
              )}
            </span>
            {showPricing
              ? <ChevronUp className="h-4 w-4" style={{ color: Colors.text.secondary }} />
              : <ChevronDown className="h-4 w-4" style={{ color: Colors.text.secondary }} />}
          </button>

          {showPricing && (
            <div className="mt-2">
              {existingPrices.map((cp) => (
                <div key={cp.customer_id} className="flex items-center justify-between gap-2 border-b py-2" style={{ borderColor: Colors.border.subtle }}>
                  <span className="min-w-0">
                    <span className="block truncate text-[14px]" style={{ color: Colors.text.primary }}>{cp.customer_name}</span>
                    <span className="block text-[12px] font-semibold" style={{ color: Colors.semantic.positive }}>₹{cp.price_per_piece?.toFixed(2)}/piece</span>
                  </span>
                  <button
                    type="button"
                    onClick={() => onRemovePrice(cp.customer_id)}
                    aria-label={`Remove price for ${cp.customer_name}`}
                    className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full border"
                    style={{ borderColor: Colors.border.default, color: Colors.status.error }}
                  >
                    <X className="h-4 w-4" />
                  </button>
                </div>
              ))}

              <Field label="Customer" className="mt-3">
                <SearchBar placeholder="Search customers…" onChange={onCustomerSearch} testID="customer-search-input" />
                <div className="mt-2">
                  <SelectField name="customer" label="Customer" value={newPriceCustomer} options={customerOptions} onChange={setNewPriceCustomer} placeholder="Select customer…" />
                </div>
              </Field>
              <Field label="Price per piece (₹)" className="mt-3">
                <input
                  type="number" inputMode="decimal" step="0.01" value={newPriceValue} placeholder="0.00"
                  onChange={(e) => setNewPriceValue(e.target.value)}
                  data-testid="price-per-piece" className={`${INPUT_CLASS} text-right tabular-nums`} style={inputStyle}
                />
              </Field>
              <button
                type="button"
                onClick={onAddPrice}
                data-testid="add-price-btn"
                className="mt-3 flex h-11 w-full items-center justify-center gap-1.5 rounded-[10px] border text-[13px] font-semibold"
                style={{ backgroundColor: Colors.background.primary, borderColor: Colors.border.default, color: Colors.accent.DEFAULT }}
              >
                <Plus className="h-4 w-4" /> Add price
              </button>
            </div>
          )}
        </Card>

        <PrimaryButton className="mt-5" onClick={onSubmit} busy={saving} disabled={!itemName} testID="save-item-btn">
          <Save className="h-4 w-4" /> Save changes
        </PrimaryButton>
      </div>
    </PhonePage>
  );
}
